<template>
  <div class="account">
    <h1 class="account__title">{{ pageHeader }}</h1>
    <tz-spinner
      v-if="isLoadingItems"
      :is-check="false"
      spinner-size="140"
      :spinner-animation="true"
      spinner-color="#4476ff"
      spinner-stroke="2"
      :spinner-delay="1000"
      :is-starting-spin="true"
    />
    <div
      v-if="!isLoadingItems && msgTitle"
      class="account__content account__content--msg"
      :class="{ 'account__content--cancel': canceledData }"
    >
      <h3>{{ msgTitle }}</h3>
      <p>{{ msgSubtitle }}</p>
      <p v-html="cms.page.contact_support_message" />
    </div>
    <div v-if="!isLoadingItems" class="account__content">
      <ic-section-header
        title="Personal Info"
        :clickEvent="() => handleEditButtonClick('personalInfo')"
      />
      <div class="personal-info personal-info--name" v-if="currentUser">
        <h3 class="personal-info__header">Name</h3>
        <span class="personal-info__content">{{ accountName }}</span>
        <button
          class="personal-info__button"
          v-touch:tap="() => handleEditButtonClick('personalInfo')"
        >
          {{ editButtonText }}
        </button>
      </div>
      <div class="personal-info personal-info--email" v-if="currentUser">
        <h3 class="personal-info__header">Email</h3>
        <span class="personal-info__content">{{ currentUser.email }}</span>
        <button
          class="personal-info__button"
          v-touch:tap="() => handleEditButtonClick('personalEmail')"
        >
          {{ editButtonText }}
        </button>
      </div>
      <div class="personal-info personal-info--address" v-if="currentUser">
        <h3 class="personal-info__header">Address</h3>
        <p class="personal-info__content" v-html="address" />
        <button
          class="personal-info__button"
          v-touch:tap="() => handleEditButtonClick('personalAddress')"
        >
          {{ editButtonText }}
        </button>
      </div>
      <ic-section-header
        title="Password"
        :buttonText="editButtonText"
        :clickEvent="() => handleEditButtonClick('password')"
      />
      <div class="masked-password">{{ passwordMask }}</div>
      <template v-if="hasReportMonitoring">
        <ic-section-header title="Report Monitoring">
          <template>
            <tz-switch
              class="tz-switch--account"
              :is-toggled="receiveMonitorNotifications"
              name="monitor_report"
              label=""
              @tz-switch-change="handleToggleSwitch"
            />
          </template>
        </ic-section-header>
        <span class="personal-info__content">
          Receive email notifications when there's an update on a report.
        </span>
      </template>
      <ic-section-header
        class="ic-section-header--payment"
        title="Payment Info"
        :buttonText="addButtonText"
        :clickEvent="() => handleEditButtonClick('paymentInfo')"
      />
      <div
        class="payment-info"
        v-if="payments.length"
        v-for="(payment, index) in payments"
        :key="`payment_${index}`"
      >
        <div class="payment-info__processor">
          <img
            class="payment-info__img"
            :class="getPaymentClass({ type: payment.type })"
            :src="handlePaymentImage(payment.type?.toLowerCase())"
            alt="The logo of the payment processor for the payment card on file"
          />
        </div>
        <ul
          class="payment-info__details"
          :class="{
            'payment-info__details--paypal':
              (payment.kind && payment.kind?.toLowerCase() === 'paypal') ||
              (payment.kind === 'token' && payment.type === 'paypal'),
            'payment-info__details--default': payment.preferred_payment
          }"
        >
          <li v-if="cardName(payment)" class="payment-info__container-column">
            <h3 class="payment-info__title">
              <span v-if="!/sprel|paypal|token/.test(payment.type)"
                >Cardholder </span
              >Name
            </h3>
            <span class="payment-info__content">
              {{ cardName(payment) }}
            </span>
          </li>
          <li
            v-if="!/sprel|paypal|token/.test(payment.type)"
            class="payment-info__container-column"
          >
            <h3 class="payment-info__title">Card Number</h3>
            <span class="payment-info__content">
              **** **** **** {{ cardLastFour(payment) }}
            </span>
          </li>
          <li
            v-if="
              (payment.kind && payment.kind?.toLowerCase() === 'paypal') ||
                (payment.kind === 'token' && payment.type === 'paypal')
            "
            class="payment-info__container-column"
          >
            <h3 class="payment-info__title">Email</h3>
            <span class="payment-info__content">
              {{ getPaypalEmail({ paypalPayment: payment }) }}
            </span>
          </li>
          <li
            v-if="!/sprel|paypal|token/.test(payment.type)"
            class="payment-info__container-column"
          >
            <h3 class="payment-info__title">Expiration Date</h3>
            <span class="payment-info__content">
              {{ payment.expire_month }}/{{ payment.expire_year }}
            </span>
          </li>
          <li
            class="payment-info__container-column"
            v-if="!payment.preferred_payment"
          >
            <div class="payment-info__buttons">
              <button
                class="payment-info__button"
                v-touch:tap="() => updateDefaultPayment(payment)"
              >
                Set as Default Payment
              </button>
              <button
                class="payment-info__button"
                v-touch:tap="() => handleDeletePaymentClick(payment)"
              >
                Delete Payment
              </button>
            </div>
          </li>
        </ul>
      </div>
      <p v-if="!payments.length">{{ noPaymentMessage }}</p>
      <ic-section-header title="Billing Details" />
      <p v-if="!subscriptions.length && !billingHistory">
        {{ noBillingDetailsText }}
      </p>
      <div v-else>
        <h3 class="billing-history__title" v-if="subscriptions.length">
          Membership
        </h3>
        <div
          class="inventory"
          v-if="subscriptions.length && !subscription.hide"
          v-for="(subscription, index) in subscriptions"
          :key="`inventory-subscription_${index}`"
        >
          <div class="inventory__content">
            <span class="inventory__name">
              {{ subscription.name }}
            </span>
            <div class="inventory__info">
              {{
                subscription.autorenew ? 'Next Billing Date:' : 'Expires On:'
              }}
              <span class="inventory__renewal">
                {{
                  formatNextPaymentDateAndTime({
                    date: subscription.expires
                      ? subscription.expires
                      : subscription.created_at,
                    expires: !!subscription.expires
                  })
                }}
              </span>
            </div>
          </div>
          <button
            v-if="showCancelAction && subscription.editText"
            class="inventory__edit"
            v-touch:tap="() => handleCancelAction(subscription)"
          >
            {{ subscription.editText }}
          </button>
        </div>
        <h3 class="billing-history__title" v-if="products.length">
          Add-ons
        </h3>
        <div
          class="inventory"
          v-if="products.length"
          v-for="(product, index) in products"
          :key="`inventory-product_${index}`"
        >
          <div class="inventory__content">
            <span class="inventory__name">
              {{ product.name }}
            </span>
            <div class="inventory__info">
              {{ product.description }}
            </div>
          </div>
        </div>

        <div
          class="billing-history"
          v-if="billingHistory && typeof billingHistory === 'object'"
        >
          <h3 class="billing-history__title">Billing History</h3>
          <div class="billing-history__container">
            <div class="billing-history__container-column">
              <h4 class="billing-history__subtitle">Date</h4>
              <span class="billing-history__data">
                {{ this.formatDate(billingHistory.created_at) }}
              </span>
            </div>
            <div class="billing-history__container-column">
              <h4 class="billing-history__subtitle">Type</h4>
              <span class="billing-history__data">
                {{ billingType }}
              </span>
            </div>
            <div class="billing-history__container-row">
              <div
                class="
                billing-history__container-column billing-history__space-right
              "
              >
                <h4 class="billing-history__subtitle">Billed to</h4>
                <span class="billing-history__data">
                  {{ billedTo }}
                </span>
              </div>
              <div class="billing-history__container-column">
                <h4 class="billing-history__subtitle">Total</h4>
                <span class="billing-history__data">
                  {{
                    `$${Number.parseFloat(
                      billingHistory.full_total.replace('$', '')
                    ).toFixed(2)}`
                  }}
                </span>
              </div>
            </div>
          </div>
          <router-link
            :to="{ name: 'dashboard-billing-history' }"
            class="billing-history__button"
          >
            {{ viewBillingHistoryText }}
          </router-link>
        </div>
      </div>
    </div>
    <ic-account-modal
      :isModalOpen="isPaymentModalOpen"
      @ic-account-modal-close="closeModal('paymentInfo')"
      v-if="currentUser"
    >
      <ic-add-payment
        :userId="currentUser.id"
        :domain="currentUser.domain"
        :heading="addPaymentHeading"
        :submitBtnText="addPaymentSubmitText"
        :forceDefaultPayment="shouldRetryRebill"
        :shouldChargeCardImmediately="shouldRetryRebill"
        :lineItems="retryRebillLineItems"
        :warningText="addPaymentWarning"
        :billing-version="currentUser?.user_data?.v2 ? 'v2' : 'v1'"
        @ic-account-modal-close="closeModal('paymentInfo')"
        @add-payment="addPayment"
        @successful-retry-rebill="handleRetryRebillSuccess"
        @failed-retry-rebill="handleRetryRebillFailure"
      />
    </ic-account-modal>
    <ic-account-modal
      :is-modal-open="isPersonalModalOpen"
      @ic-account-modal-close="closeModal('personalInfo')"
    >
      <ic-edit-name
        :first-name="prefillNames.firstName"
        :middle-name="prefillNames.middleName"
        :last-name="prefillNames.lastName"
        :submit-button-text="updateButtonText"
        :cancel-button-text="cancelButtonText"
        :is-submitting="isUpdatingName"
        :errors="inputNameError"
        @ic-edit-name-close-modal="closeModal('personalInfo')"
        @update-first-name="updateFirstName"
        @update-middle-name="updateMiddleName"
        @update-last-name="updateLastName"
      />
    </ic-account-modal>
    <ic-account-modal
      :is-modal-open="isEditEmailModalOpen"
      @ic-account-modal-close="closeModal('personalEmail')"
    >
      <ic-edit-email
        :submit-button-text="updateButtonText"
        :cancel-button-text="cancelButtonText"
        :is-submitting="isUpdatingEmail"
        :error-body="emailErrorBody"
        @ic-edit-email-close-modal="closeModal('personalEmail')"
        @ic-edit-email-update="updateEmailAddress"
      />
    </ic-account-modal>
    <ic-account-modal
      :is-modal-open="isDeletePaymentModalOpen"
      @ic-account-modal-close="closeModal('deletePayment')"
      v-if="selectedPayment"
    >
      <ic-delete-payment
        :user-id="currentUser.id"
        :heading="deletePaymentModalHeading"
        :delete-payment-question="deletePaymentQuestion"
        :payment="selectedPayment"
        :delete-button-text="deleteButtonText"
        :cancel-button-text="cancelButtonText"
        :payment-image="handlePaymentImage(selectedPayment.type)"
        :cardName="cardName(selectedPayment)"
        :billing-version="currentUser.user_data?.v2 ? 'v2' : 'v1'"
        :domain="currentUser.domain"
        @ic-account-modal-close="closeModal('deletePayment')"
        @delete-payment="deletePayment"
      ></ic-delete-payment>
    </ic-account-modal>

    <ic-account-modal
      class="ic-account-modal--cancelation-confirmation"
      v-if="subscriptionToCancel"
      :is-modal-open="isSubscriptionCancelationModalOpen"
      @ic-account-modal-close="closeModal('subscriptionCancelation')"
    >
      <div class="ic-modal-body">
        <h3 class="ic-modal-body__title">
          Are you sure you want to cancel your ${{
            subscriptionToCancel.price
          }}/month {{ subscriptionToCancel.name }} subscription which includes
          all of the following features?
        </h3>
        <ul class="ic-modal-body__list">
          <li
            v-for="(unlockable, index) in subscriptionToCancel.unlockables"
            :key="`unlockable__${index}`"
            class="ic-modal-body__list-item"
            v-text="
              formatNameCaseWithSplitterAndSpacer({
                name: unlockable.name,
                separator: '_'
              })
            "
          />
        </ul>
        <div class="ic-modal-body__form-buttons">
          <button
            class="ic-modal-body__form-button ic-modal-body__form-button--cancel"
            v-touch:tap="() => closeModal('subscriptionCancelation')"
          >
            No, I want to keep it
          </button>
          <button
            class="ic-modal-body__form-button ic-modal-body__form-button--confirm"
            v-touch:tap="() => handleEditSubscription(subscriptionToCancel)"
          >
            Yes, cancel now
          </button>
        </div>
      </div>
    </ic-account-modal>
  </div>
</template>

<script>
import Meta from '@/assets/js/shared/misc/meta.mjs';
import helpers from '@/assets/js/shared/misc/helpers';
import IcSectionHeader from '@/components/ic-section-header';
import IcAddPayment from '@/components/ic-add-payment';
import IcDeletePayment from '@/components/ic-delete-payment';
import IcAccountModal from '@/components/ic-account-modal';
import IcEditName from '@/components/ic-edit-name';
import IcEditEmail from '@/components/ic-edit-email';
import IcEditSubscription from '@/components/ic-edit-subscription';
import TzSpinner from '@trazi/tz-spinner/src/tz-spinner.vue';
import TzSwitch from '@trazi/tz-switch/src/tz-switch.vue';
import billingSDK from '@/assets/js/shared/services/billing-sdk';
import billingV2SDK from '@/assets/js/shared/services/billing-v2-sdk';
import setTitleCase from '@/assets/js/shared/helpers/setTitleCase';
import getPaypalEmail from '@/assets/js/shared/helpers/getPaypalEmail';
import getFormattedMultilineAddressOrUnknown from '@/assets/js/shared/helpers/getFormattedMultilineAddressOrUnknown';
import formatNameCaseWithSplitterAndSpacer from '@/assets/js/shared/helpers/formatNameCaseWithSplitterAndSpacer';
import getPaymentClass from '@/assets/js/helpers/getPaymentClass';
import onLayoutReady from '../../mixins/onLayoutReady';
import formatFullName from '@/assets/js/shared/helpers/formatFullName';
import { mapGetters, mapActions } from 'vuex';

const NBars = require('nbars/commonjs.js');
const cms = require('@/assets/cms/pages/dashboard/dashboard-account.json');
const imageVisa = require('@/assets/images/shared/payment-processors/visa.svg');
const imageMastercard = require('@/assets/images/shared/payment-processors/mastercard.svg');
const imagePaypal = require('@/assets/images/shared/paypal.svg');

export default {
  name: 'dashboard-account',
  layout: 'dashboard',
  mixins: [onLayoutReady],
  components: {
    IcEditEmail,
    IcEditName,
    IcAddPayment,
    IcDeletePayment,
    IcEditSubscription,
    IcAccountModal,
    IcSectionHeader,
    TzSpinner,
    TzSwitch
  },
  data() {
    return {
      initializedBillingSDK: null,
      editButtonText: cms.buttons.edit,
      viewBillingHistoryText: cms.buttons.billing,
      addButtonText: cms.buttons.add,
      deletePaymentModalHeading: cms.delete_payment_modal_header,
      deletePaymentQuestion: cms.delete_payment_question,
      deleteButtonText: cms.buttons.delete,
      updateButtonText: cms.buttons.update,
      cancelButtonText: cms.buttons.cancel,
      noPaymentMessage: cms.no_payment_message,
      noBillingDetailsText: cms.no_billing_details_text,
      pageHeader: cms.page_header,
      passwordMask: '*******',
      accountSubscription: null,
      billingHistory: null,
      billedTo: null,
      isPaymentModalOpen: false,
      isDeletePaymentModalOpen: false,
      isSubscriptionCancelationModalOpen: false,
      subscriptionToCancel: null,
      selectedPayment: null,
      payments: null,
      isPersonalModalOpen: false,
      isUpdatingName: false,
      inputNameError: {
        firstName: '',
        middleName: '',
        lastName: ''
      },
      isEditEmailModalOpen: false,
      isUpdatingEmail: false,
      emailErrorBody: '',
      subscriptions: [],
      products: [],
      isEditSubscriptionModalOpen: false,
      renewSubscriptionText: cms.subscription.renew_text,
      cancelSubscriptionText: cms.subscription.cancel_text,
      subscriptionConfirmText: cms.subscription.confirm_modal_text,
      subscriptionCancelText: cms.subscription.close_modal_text,
      subscriptionPerksRenew: cms.subscription.perks_heading_renew,
      subscriptionPerksCancel: cms.subscription.perks_heading_cancel,
      subscriptionPerks: cms.subscription.perks,
      subscriptionToEdit: null,
      isEditingSubscription: false,
      subscriptionErrorBody: null,
      isLoadingItems: true,
      activeSubscription: null,
      isLoadingSubscriptionData: true,
      receiveMonitorNotifications: true,
      addPaymentHeading: undefined,
      addPaymentSubmitText: undefined,
      addPaymentWarning: undefined,
      shouldForceDefaultPayment: false,
      shouldRetryRebill: false,
      retryRebillLineItems: null,
      getFormattedMultilineAddressOrUnknown,
      getPaypalEmail,
      formatNameCaseWithSplitterAndSpacer,
      defaultSubscriptionType: 'default',
      canceledData: null,
      msgTitle: null,
      msgSubtitle: null,
      retryInfo: null
    };
  },
  computed: {
    ...mapGetters('auth', ['currentUser', 'hasEnabledMonitoring']),
    billingType() {
      if (this.billingHistory && this.billingHistory.line_items) {
        const { line_items } = this.billingHistory;
        if (line_items.length === 0) {
          return '';
        }
        return line_items[0].name;
      }
    },
    accountName() {
      return formatFullName({
        firstName: this.currentUser.firstname,
        middleName: this.currentUser.middlename,
        lastName: this.currentUser.lastname,
        titleCase: true
      });
    },
    hasReportMonitoring() {
      const hasMonitoringPermission = this.hasEnabledMonitoring;

      return this.$route.query.test && hasMonitoringPermission;
    },
    address() {
      return getFormattedMultilineAddressOrUnknown({
        address: this.$parent.$attrs.currentUser,
        default: 'Unknown'
      });
    },
    showCancelAction() {
      return this.$parent.$parent.showCancelSubscriptionAction;
    },
    prefillNames() {
      return {
        firstName: setTitleCase({ text: this.currentUser.firstname }),
        middleName: setTitleCase({ text: this.currentUser.middlename }),
        lastName: setTitleCase({ text: this.currentUser.lastname })
      };
    }
  },
  methods: {
    ...mapActions('auth', ['checkUserPermissions']),
    getPaymentClass,
    /**
     * Formats card name
     * @param payment
     * @returns string
     */
    cardName(payment) {
      let fullname = '';
      if (payment.firstname) {
        fullname += payment.firstname;
      }
      if (payment.middlename) {
        fullname += ' ' + payment.middlename;
      }
      if (payment.lastname) {
        fullname += ' ' + payment.lastname;
      }
      return helpers.formatName(fullname) || 'Unknown';
    },
    /**
     * Returns the last four digits of the card number
     * @param payment
     * @returns {string|null}
     */
    cardLastFour(payment) {
      const hasVirtualCard =
        payment?.type?.includes('apple') || payment?.type?.includes('google');
      const realLastFour =
        payment?.name?.split(' ')[1] || payment?.extra_tracking?.realLastFour;
      const lastFour = payment?.card_check_digit;

      return hasVirtualCard ? realLastFour : lastFour;
    },
    /**
     * Hits endpoint to update the user's email
     *
     * @param newEmail
     * @return {Promise}
     */
    async updateEmailAddress(newEmail) {
      this.isUpdatingEmail = true;

      try {
        const response = await this.$travellingSDK.UserCurrent.editPropertyValue(
          'email',
          newEmail
        );

        if (
          response.type === 'email-error' ||
          response.type === 'exists-error'
        ) {
          throw response.msg;
        }

        this.$store.commit('auth/setEmail', response);
        this.isEditEmailModalOpen = false;
        this.isUpdatingEmail = false;
        this.emailErrorBody = '';
      } catch (err) {
        this.isEditEmailModalOpen = true;
        this.isUpdatingEmail = false;
        this.emailErrorBody = err;
        this.$bugsnag?.notify(err, event => {
          event.addMetadata('vue', {
            page: 'dashboard/account.vue',
            method: 'updateEmailAddress',
            newEmail
          });
        });
      }
    },
    /**
     * Hits endpoint to update the user's first name
     *
     * @param updatedFirstName
     * @return {Promise}
     */
    async updateFirstName(updatedFirstName) {
      this.isUpdatingName = true;
      try {
        const res = await this.updateProp('firstname', updatedFirstName);

        /** If call is successful, check for errors **/
        if (res.type === 'firstname-error' && res.msg) {
          this.isUpdatingName = false;
          this.inputNameError.firstName = error.message;
          return;
        }

        this.$store.commit('auth/setFirstName', res);
        this.isPersonalModalOpen = false;
        this.isUpdatingName = false;
      } catch (err) {
        this.inputNameError.firstName = err.message;
        this.isUpdatingName = false;
        this.$bugsnag?.notify(err, event => {
          event.addMetadata('vue', {
            page: 'dashboard/account.vue',
            method: 'updateFirstName',
            updatedFirstName
          });
        });
      }
    },
    /**
     * Hits endpoint to updates the user's middle name
     *
     * @param {string} updatedMiddleName
     * @return {Promise}
     */
    async updateMiddleName(updatedMiddleName) {
      this.isUpdatingName = true;
      try {
        const res = await this.updateProp('middlename', updatedMiddleName);

        // When removing middlename, we'll want to set it to an empty string
        // when response returns 'null'.
        // Will prevent having the user from refreshing page to see the change
        if (res === null) {
          this.$store.commit('auth/setMiddleName', '');
        }

        /** If call is successful, check for errors **/
        if (res.type === 'middlename-error' && res.msg) {
          this.isUpdatingName = false;
          this.inputNameError.middleName = res.msg;
          return;
        }

        this.$store.commit('auth/setMiddleName', res);
        this.isPersonalModalOpen = false;
        this.isUpdatingName = false;
      } catch (err) {
        /** if middle name field is prefilled and removed when submitting, close modal after middle name is set to null **/
        this.$bugsnag?.notify(err, event => {
          event.addMetadata('vue', {
            page: 'dashboard/account.vue',
            method: 'updateMiddleName',
            updatedMiddleName
          });
        });
        if (
          err.message === 'res is null' ||
          err.message === 'r is null' ||
          err.message === "null is not an object (evaluating 'res.type')" ||
          "Cannot read properties of null (reading 'type')"
        ) {
          this.inputNameError.middleName = '';
          this.isUpdatingName = false;
          this.isPersonalModalOpen = false;
          return;
        }
        this.inputNameError.middleName = err.message;
        this.isUpdatingName = false;
      }
    },
    /**
     * Hits endpoint to update the user's last name
     *
     * @param {string} updatedLastName
     * @return {Promise}
     */
    async updateLastName(updatedLastName) {
      this.isUpdatingName = true;
      try {
        const res = await this.updateProp('lastname', updatedLastName);

        /** If call is successful, check for errors **/
        if (res.type === 'lastname-error' && res.msg) {
          this.isUpdatingName = false;
          this.inputNameError.lastName = res.msg;
          return;
        }

        this.$store.commit('auth/setLastName', res);
        this.isPersonalModalOpen = false;
        this.isUpdatingName = false;
      } catch (err) {
        this.inputNameError.lastName = err.message;
        this.isUpdatingName = false;
        this.$bugsnag?.notify(err, event => {
          event.addMetadata('vue', {
            page: 'dashboard/account.vue',
            method: 'updateLastName',
            updatedLastName
          });
        });
      }
    },
    /**
     * Updates the property value
     *
     * @param {string} name
     * @param {string} value
     */
    async updateProp(name, value) {
      try {
        const res = await this.$travellingSDK.UserCurrent.editPropertyValue(
          name,
          value,
          null
        );
        // If error then reject promise
        if (res === 'Access Denied') {
          throw Error('Unable to update the field');
        }
        return res;
      } catch (err) {
        this.$bugsnag?.notify(err, event => {
          event.addMetadata('vue', {
            page: 'dashboard/account.vue',
            method: 'updateProp',
            name,
            value
          });
        });
        throw {
          name,
          message: err.message || 'Error updating the field'
        };
      }
    },
    /**
     * Updates default payment
     * @param {object} selectedPayment
     */
    async updateDefaultPayment(selectedPayment) {
      try {
        if (!this.currentUser.user_data?.v2) {
          await this.initializedBillingSDK.Payments.setPreferredByAccount(
            this.currentUser.domain,
            this.currentUser.id,
            selectedPayment.id
          );
        } else {
          await this.$billingProcessorSDK.Payment.setPreferred(
            this.currentUser.domain,
            this.currentUser.id,
            selectedPayment.id
          );
        }

        for (let i = 0; i < this.payments.length; i++) {
          if (
            this.payments[i].preferred_payment &&
            this.payments[i].id !== selectedPayment.id
          ) {
            this.payments[i].preferred_payment = false;
          }
          if (this.payments[i].id === selectedPayment.id) {
            this.payments[i].preferred_payment = true;
          }
        }
        this.$parent.$parent.sendSuccessToast('Default Payment Updated');
      } catch (err) {
        console.error(err);
        this.$bugsnag?.notify(err, event => {
          event.addMetadata('vue', {
            page: 'dashboard/account.vue',
            method: 'updateDefaultPayment',
            personalInfo: this.currentUser,
            selectedPayment
          });
        });
      }
    },
    /**
     * Deletes payment from payments
     * @param {string} paymentId
     */
    deletePayment(paymentId) {
      this.payments.forEach((payment, index) => {
        if (paymentId === payment.id) {
          this.payments.splice(index, 1);
        }
      });
      this.$toast.show('Your payment has been deleted.', {
        className: 'account__toast',
        duration: 1200
      });
    },
    /**
     * Closes each modal individually
     * @param {string} type
     */
    closeModal(type) {
      switch (type) {
        case 'paymentInfo':
          this.isPaymentModalOpen = false;
          this.addPaymentHeading = undefined;
          this.addPaymentSubmitText = undefined;
          this.addPaymentWarning = undefined;
          this.shouldRetryRebill = false;
        case 'personalInfo':
          // prevent user from closing modal when clicking on the scrim when form is being submitted.
          if (this.isUpdatingName) {
            return;
          }
          this.isPersonalModalOpen = false;
          this.inputNameError.firstName = '';
          this.inputNameError.middleName = '';
          this.inputNameError.lastName = '';
          break;
        case 'personalEmail':
          this.isEditEmailModalOpen = false;
          this.emailErrorBody = '';
          break;
        case 'deletePayment':
          this.isDeletePaymentModalOpen = false;
        case 'editSubscription':
          this.subscriptionErrorBody = null;
          this.isEditSubscriptionModalOpen = false;
          break;
        case 'subscriptionCancelation':
          this.isSubscriptionCancelationModalOpen = false;
          break;
      }
    },
    /**
     * Adds a new payment method to the payments list.
     * - If `addedPayment` is marked as preferred, sets it as the preferred payment.
     * - If travelling v2 we need to fetch the full payment details before adding.
     * - Otherwise, adds `addedPayment` directly to the payments list.
     *
     * @param {Object} addedPayment - The payment object to add.
     * @param {string} [addedPayment.id] - The ID of the payment method.
     * @param {boolean} [addedPayment.preferred_payment] - Indicates if this is the preferred payment method.
     */
    async addPayment(addedPayment) {
      if (!addedPayment) return;

      if (addedPayment.preferred_payment) {
        this.setPreferredPayment(addedPayment);
        return;
      }

      if (this.currentUser.user_data?.v2) {
        addedPayment = await this.fetchPaymentById(addedPayment.id);
      }

      this.payments.push(addedPayment);
    },
    /**
     * Fetches a payment method's full details by its ID.
     *
     * @param {string} paymentId - The ID of the payment method to fetch.
     * @returns {Promise<Object>} - A promise that resolves to the payment object.
     */
    async fetchPaymentById(paymentId) {
      const domain =
        this.$config.environment === 'production'
          ? `${this.$config.website}.com`
          : this.$config.testingAccountDomain;

      return await this.$billingProcessorSDK.Payment.List.byId(
        domain,
        paymentId
      );
    },
    /**
     * Sets the specified payment method as the preferred payment.
     * Moves the payment to the top of the payments list and unmarks any existing preferred payment.
     *
     * @param {Object} payment - The payment object to set as preferred.
     */
    setPreferredPayment(payment) {
      const currentPreferred = this.payments.find(p => p.preferred_payment);
      if (currentPreferred) {
        currentPreferred.preferred_payment = false;
      }
      this.payments.unshift(payment);
    },
    /**
     * Pass in unformatted date to get formatted Next Billing Date
     * @param {object} opts
     * @param {string} opts.date
     * @param {boolean} opts.expires
     * @returns string
     */
    formatNextPaymentDateAndTime(opts = {}) {
      const { date, expires } = opts;

      return `${this.formatNextPaymentDate({
        date,
        expires
      })} at ${this.formatNextPaymentTime(date)}`;
    },
    /**
     *
     * @param {object} opts
     * @param {string} opts.date
     * @param {boolean} opts.expires
     * @returns {string}
     */
    formatNextPaymentDate(opts = {}) {
      const { date, expires } = opts;
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ];
      const time = new Date(date);
      expires
        ? time.setMonth(time.getMonth())
        : time.setMonth(time.getMonth() + 1);
      const dd = time
        .getDate()
        .toString()
        .padStart(2, '0');
      const mm = monthNames[time.getMonth()];
      const yyyy = time.getFullYear();
      return `${mm} ${dd}, ${yyyy}`;
    },
    formatNextPaymentTime(date) {
      const time = new Date(date);
      time.setMonth(time.getMonth() + 1);
      let hour = time.getHours();
      const minutes = time
        .getMinutes()
        .toString()
        .padStart(2, '0');
      let daytime = 'AM';
      if (hour > 12) {
        hour = hour - 12;
        daytime = 'PM';
      }
      return `${hour}:${minutes} ${daytime}`;
    },
    /**
     * Pass in unformatted date to get formatted Billing Details Date
     * @param date
     * @returns string
     */
    formatDate(date) {
      const time = new Date(date);
      let dd = time.getDate();
      let mm = time.getMonth() + 1;
      const yyyy = time.getFullYear();
      if (dd < 10) {
        dd = `0${dd}`;
      }
      if (mm < 10) {
        mm = `0${mm}`;
      }
      return `${mm}/${dd}/${yyyy}`;
    },
    /**
     * Pass in payment object to get back a formatted Billed To string
     * @param payment
     * @returns string
     */
    formatBilledTo(payment) {
      const cardName = payment.type.toUpperCase();
      return `${cardName} - ${this.cardLastFour(payment)}`;
    },
    /**
     * Pass in type of credit card to get back appropriate image
     * @param type
     * @returns {*}
     */
    handlePaymentImage(type) {
      switch (type) {
        case 'master':
        case 'mastercard':
          return imageMastercard;
        case 'paypal':
        case 'sprel':
          return imagePaypal;
        default:
          return imageVisa;
      }
    },
    handleEditButtonClick(type, data = null) {
      switch (type) {
        case 'paymentInfo':
          this.isPaymentModalOpen = true;
          break;
        case 'personalInfo':
          this.isPersonalModalOpen = true;
          break;
        case 'personalEmail':
          this.isEditEmailModalOpen = true;
          break;
        case 'personalAddress':
          this.$parent.$parent.toggleModal('personalAddress');
          break;
        case 'password':
          this.$parent.$parent.toggleModal('edit-password');
          break;
      }
    },
    handleEditSubscription(subscription) {
      // editting is canceling
      // Note subscription is combination of Subscription and InventorySubscription endpoint data
      if (subscription.editText === this.cancelSubscriptionText) {
        const retryCount = subscription.retry_count;
        const lastRetriedDate = new Date(subscription.last_retried);
        const preferredPayment = this.payments[0];
        const paymentUpdatedDate = preferredPayment.updated_at
          ? new Date(preferredPayment.updated_at)
          : new Date(preferredPayment.created_at);

        let hasFailedPayment = false;
        let hasUpdatedPayment = true;
        if (retryCount) {
          hasFailedPayment = true;
        }
        // if subscription is showing payment failed and the last time it was retried was after the account's default payment was last updated
        // they would then need to update their default payment first before doing cancel/substitute
        if (retryCount && lastRetriedDate > paymentUpdatedDate) {
          hasUpdatedPayment = false;
        }

        const subscriptionDataToStore = {
          id: subscription.id,
          price: subscription.msrp,
          frequencyLabel: subscription.frequencyLabel,
          subscriptionId: subscription.subscription_id,
          nextPaymentDate: this.formatNextPaymentDate({
            date: subscription.expires
              ? subscription.expires
              : subscription.created_at,
            expires: !!subscription.expires
          }),
          nextPaymentTime: this.formatNextPaymentTime(subscription.created_at),
          type: subscription.type,
          hasFailedPayment,
          hasUpdatedPayment,
          unlockables: subscription.unlockables
        };
        sessionStorage.setItem(
          'cancel',
          JSON.stringify(subscriptionDataToStore)
        );
        return this.$router.push({ path: '/dashboard/cancel/' });
      }
      if (subscription.type !== this.defaultSubscriptionType) {
        return this.$router.push({
          path: `/dashboard/subscribe/${subscription.type}/`
        });
      }
      return this.$router.push({ path: '/dashboard/reactivate/' });
    },
    /**
     * Pass in payment to open delete payment modal
     * @param {object} payment
     */
    handleDeletePaymentClick(payment) {
      this.selectedPayment = payment;
      this.isDeletePaymentModalOpen = true;
    },
    async getInventoryInfo(obj = {}) {
      if (!obj || !obj.type || !obj.domain || !obj.accountId) {
        return null;
      }
      const vm = this;
      return new Promise(async function(resolve, reject) {
        let res = null;
        try {
          res = await vm.initializedBillingSDK[obj.type].byId(
            obj.domain,
            obj.accountId
          );
        } catch (err) {
          console.error(err);
          this.$bugsnag?.notify(err, event => {
            event.addMetadata('vue', {
              page: 'dashboard/account.vue',
              method: 'getInventoryInfo',
              obj
            });
          });
        }
        resolve(res);
      });
    },
    handleToggleSwitch() {
      this.receiveMonitorNotifications = !this.receiveMonitorNotifications;

      if (!this.receiveMonitorNotifications) {
        this.$parent.$emit('send-toast', { type: 'disable-notifications' });
      } else {
        this.$parent.$emit('send-toast', { type: 'enable-notifications' });
      }
      //TODO: Hit endpoint to send email notifications for report monitoring
    },
    handleUpdateUrlQuery() {
      // if update=true is in url, user's payment is failing and update default payment is needed and then we retry rebill
      this.$router.push('/dashboard/account/');
      this.retryInfo = JSON.parse(sessionStorage.getItem('retry'));
      sessionStorage.removeItem('retry');
      this.addPaymentHeading = 'Update Default Payment';
      this.addPaymentSubmitText = 'Update';
      this.shouldRetryRebill = true;
      this.addPaymentWarning =
        `To gain access to unlimited ${
          this.retryInfo && this.retryInfo.type !== 'default'
            ? this.retryInfo.type
            : 'people'
        }` +
        ` search, when you update your default payment method you will be charged the amount for the subscription. You may also contact customer support at <a href="mailto:support@information.com">support@information.com</a> or call <a href="tel:1-800-915-0696">1-800-915-0696</a>, and you may reference error code 223.`;
      this.isPaymentModalOpen = true;
      this.retryRebillLineItems = [
        {
          id: this.retryInfo ? this.retryInfo.id : this.activeSubscription.id,
          qty: 1,
          type: 'inventory'
        }
      ];
    },
    async handleRetryRebillSuccess() {
      await this.checkUserPermissions();
      if (
        this.retryInfo &&
        this.retryInfo.redirectPath &&
        this.retryInfo.redirectPath !== 'header-search'
      ) {
        return await this.$router.push(this.retryInfo.redirectPath);
      }
      const searchBtn = document.querySelector('.tz-search__btn-search');
      searchBtn.click();
    },
    async handleRetryRebillFailure() {
      if (
        this.retryInfo &&
        this.retryInfo.type &&
        this.retryInfo.type === 'default'
      ) {
        return await this.$router.push({ name: 'dashboard-reactivate' });
      }
      if (
        this.retryInfo &&
        this.retryInfo.redirectPath &&
        this.retryInfo.redirectPath !== 'header-search'
      ) {
        return await this.$router.push(this.retryInfo.redirectPath);
      }
      const searchBtn = document.querySelector('.tz-search__btn-search');
      searchBtn.click();
    },
    getSubscriptionType(inventorySubscription) {
      // checking each non-default subscription type file id's against current subscription to find current subscription type
      let subscriptionType = this.defaultSubscriptionType;
      const subscriptionTypeOptions = ['email', 'address', 'phone'];
      for (let i = 0; i < subscriptionTypeOptions.length; i++) {
        const subscriptionTypeOption = subscriptionTypeOptions[i];
        const typedSubscriptionIds =
          this.$config.environment === 'production'
            ? require(`@/assets/cms/subscribe/production/information.com/${subscriptionTypeOption}.json`)
            : require(`@/assets/cms/subscribe/staging/${this.$config.testingAccountDomain}/${subscriptionTypeOption}.json`);
        const subscriptionsByBillingVersion = this.currentUser.user_data?.v2
          ? typedSubscriptionIds.related_subscriptions_v2
          : typedSubscriptionIds.related_subscriptions;

        for (let j = 0; j < subscriptionsByBillingVersion.length; j++) {
          const subscriptionId = subscriptionsByBillingVersion[j].id;
          if (inventorySubscription.subscription_id === subscriptionId) {
            subscriptionType = subscriptionTypeOption;
            break;
          }
        }
      }
      return subscriptionType;
    },
    async handleCancelAction(subscription) {
      // Handle logic if link is "Reactivate Subscription"
      if (subscription.editText === this.renewSubscriptionText) {
        // If subscription is default, redirect to reactivate page
        if (subscription.type === this.defaultSubscriptionType) {
          return this.$router.push({ path: '/dashboard/reactivate/' });
        }

        // If not default subscription, redirect to subscription page
        return this.$router.push({
          path: `/dashboard/subscribe/${subscription.type}/`
        });
      }

      // Handle logic if link is "Cancel Subscription"
      let offerId;
      this.subscriptionToCancel = subscription;
      const domain =
        this.$config.environment === 'production'
          ? `${process.env.website}.com`
          : this.$config.testingAccountDomain || 'traziventures.com';
      const path =
        this.$config.environment === 'production'
          ? require(`@/assets/cms/cancel/production/information.com/subscription.json`)
          : require(`@/assets/cms/cancel/staging/${domain}/subscription.json`);
      try {
        offerId = this.currentUser.user_data?.v2 ? path.id_v2 : path.id;
      } catch (err) {
        console.error(err);
        this.$bugsnag?.notify(err, event => {
          event.addMetadata('vue', {
            page: 'dashboard/account.vue',
            method: 'handleCancelAction',
            subscription
          });
        });
        await this.$router.push({ name: 'dashboard-account' });
      }
      let launchConfirmation = true;
      for (let i = 0; i < subscription.unlockables.length; i++) {
        if (subscription.unlockables[i].name === 'people_reports') {
          launchConfirmation = false;
          break;
        }
      }
      if (launchConfirmation || offerId === subscription.subscription_id) {
        this.isSubscriptionCancelationModalOpen = true;
      } else {
        this.handleEditSubscription(subscription);
      }
    },
    async onLayoutReady() {
      this.initializedBillingSDK = this.currentUser.user_data?.v2
        ? billingV2SDK(window)
        : billingSDK(window).Billing;
      try {
        const { id, domain } = this.currentUser;

        // Payment Info
        this.payments = [];
        try {
          let payments;
          if (!this.currentUser.user_data?.v2) {
            payments = await this.initializedBillingSDK.Payments.byAccount(
              domain,
              id
            );
          } else {
            payments = await this.$billingProcessorSDK.Payment.List.byAccount(
              domain,
              id
            );
          }
          if (
            (payments && payments.type && payments.type.includes('error')) ||
            typeof payments === 'string'
          ) {
            payments = [];
          }
          if (payments.length) {
            const preferredPaymentIndex = payments.findIndex(
              payment => payment.preferred_payment
            );
            if (preferredPaymentIndex >= 0) {
              const preferredPayment = payments[preferredPaymentIndex];
              payments.splice(preferredPaymentIndex, 1);
              payments.unshift(preferredPayment);
            }
          }
          this.payments = payments;
        } catch (err) {
          this.payments = [];
          console.error(err);
          this.$bugsnag?.notify(err, event => {
            event.addMetadata('vue', {
              page: 'dashboard/account.vue',
              method: 'mounted',
              tryCatch: 'paymentsInfo'
            });
          });
        }

        // Billing Details
        const inventoryItemsObj = this.$parent.$parent.inventoryItemsObj;

        if (inventoryItemsObj) {
          // remove duplicate products
          const savedInventoryProducts = [];
          const inventoryProducts = inventoryItemsObj.products.filter(
            inventory => {
              if (savedInventoryProducts.includes(inventory.product_id)) {
                return false;
              }
              savedInventoryProducts.push(inventory.product_id);
              return true;
            }
          );

          for (let i = 0; i < inventoryProducts.length; i++) {
            const productInfo = await this.getInventoryInfo({
              type: 'Products',
              accountId: inventoryProducts[i].product_id,
              domain: domain
            });
            if (productInfo) {
              inventoryProducts[i].name = productInfo.name;
              inventoryProducts[i].description = productInfo.description;
              this.products.push(inventoryProducts[i]);
            }
          }

          // remove duplicate subscriptions
          const savedSubscriptionsData = [];
          const inventorySubscriptions = [];
          for (let i = 0; i < inventoryItemsObj.subscriptions.length; i++) {
            const inventory = inventoryItemsObj.subscriptions[i];
            const dateToCheck = new Date(inventory.created_at);
            const idToCheck = inventory.subscription_id;
            const dataToSave = `${idToCheck}+${dateToCheck}`;
            if (!savedSubscriptionsData.length) {
              savedSubscriptionsData.push(dataToSave);
              inventorySubscriptions.push(inventory);
              continue;
            }
            for (let j = 0; j < savedSubscriptionsData.length; j++) {
              const isLastIteration = j === savedSubscriptionsData.length - 1;
              const savedData = savedSubscriptionsData[j].split('+');
              const savedId = savedData[0];
              const savedDate = new Date(savedData[1]);
              if (idToCheck === savedId && dateToCheck < savedDate) {
                break;
              }
              if (idToCheck === savedId && dateToCheck > savedDate) {
                savedSubscriptionsData.splice(j, 1, dataToSave);
                inventorySubscriptions.splice(j, 1, inventory);
                break;
              }
              if (isLastIteration) {
                savedSubscriptionsData.push(dataToSave);
                inventorySubscriptions.push(inventory);
                break;
              }
            }
          }

          const subscriptionProductIds = [];
          for (let i = 0; i < inventorySubscriptions.length; i++) {
            const subscriptionInfo = await this.getInventoryInfo({
              type: 'Subscriptions',
              accountId: inventorySubscriptions[i].subscription_id,
              domain: domain
            });
            if (subscriptionInfo) {
              const infoToAdd = {
                editText: inventorySubscriptions[i].autorenew
                  ? this.cancelSubscriptionText
                  : this.getSubscriptionType(inventorySubscriptions[i]) ===
                    this.defaultSubscriptionType
                  ? null
                  : this.renewSubscriptionText,
                perksHeading: inventorySubscriptions[i].autorenew
                  ? this.subscriptionPerksCancel
                  : this.subscriptionPerksRenew,
                isRenew: !inventorySubscriptions[i].autorenew ?? false,
                name: subscriptionInfo.name,
                frequencyLabel: subscriptionInfo.display_frequency_label,
                type: this.getSubscriptionType(inventorySubscriptions[i])
              };
              if (inventorySubscriptions[i].autorenew) {
                this.activeSubscription = inventorySubscriptions[i];
              }
              this.subscriptions.push(
                Object.assign(inventorySubscriptions[i], infoToAdd)
              );
            }
            if (subscriptionInfo?.products?.length) {
              for (let i = 0; i < subscriptionInfo.products.length; i++) {
                subscriptionProductIds.push(subscriptionInfo.products[i]);
              }
            }
          }

          // removing products that are included in a subscription
          this.products = this.products.filter(product => {
            if (subscriptionProductIds.includes(product.product_id)) {
              return false;
            }
            return true;
          });
        }
        this.isLoadingSubscriptionData = false;
        // Billing History
        let transactions = await this.initializedBillingSDK.Transactions.byAccount(
          domain,
          id,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          'status=CAPTURED||status=AUTHORIZED||status=CANCELED||status=GIFTED||status=PARTIAL-CAPTURE||status=REFUNDED||status=PROCESSING||status=SUBSTITUTE||status=VOIDED',
          'created_at',
          true
        );

        const transactionAccessDenied =
          typeof transactions === 'string' &&
          transactions.includes('Access Denied');

        if (
          !transactions ||
          (transactions &&
            transactions.type &&
            transactions.type.includes('error')) ||
          transactionAccessDenied
        ) {
          transactions = [];
        }

        if (transactionAccessDenied) {
          this.$bugsnag?.notify(
            new Error('Transaction Access Denied'),
            event => {
              event.addMetadata('vue', {
                page: 'dashboard/account.vue',
                method: 'mounted',
                api: 'Transactions.byAccount',
                userId: this.currentUser.id
              });
            }
          );
        }

        this.billingHistory = transactions.length ? transactions[0] : null;
        if (
          transactions.length &&
          transactions[0].kind &&
          transactions[0].kind?.toLowerCase() === 'paypal'
        ) {
          for (let i = 0; i < this.payments.length; i++) {
            const payment = this.payments[i];
            if (this.billingHistory.payment_id === payment.id) {
              this.billedTo = `PayPal - ${getPaypalEmail({
                paypalPayment: payment
              })}`;
            }
          }
        } else if (
          transactions.length &&
          transactions[0].kind &&
          transactions[0].kind?.toLowerCase() === 'credit_card'
        ) {
          this.billedTo = `${setTitleCase({
            text: this.billingHistory.type
          })} - ${this.cardLastFour(this.billingHistory)
            .toString()
            .padStart(4, '0')}`;
        } else {
          this.billedTo = 'Unknown';
        }
      } catch (err) {
        console.error(err);
        this.$bugsnag?.notify(err, event => {
          event.addMetadata('vue', {
            page: 'dashboard/account.vue',
            method: 'mounted'
          });
        });
      }
      this.isLoadingItems = false;
    }
  },
  async mounted() {
    const downsellData = JSON.parse(sessionStorage.getItem('downsellData'));
    if (downsellData) {
      sessionStorage.removeItem('downsellData');
      this.msgTitle = this.cms.page.downsell_purchase.title;
      this.msgSubtitle = NBars.transform(
        this.cms.page.downsell_purchase.subtitle,
        {
          price: downsellData.price,
          duration: downsellData.duration
        }
      );
    }

    this.canceledData = JSON.parse(sessionStorage.getItem('canceledData'));
    if (this.canceledData) {
      sessionStorage.removeItem('canceledData');
      this.msgTitle = this.cms.page.cancel_completion.title;
      this.msgSubtitle = NBars.transform(
        this.cms.page.cancel_completion.subtitle,
        {
          dateTime: `${this.canceledData.expireDate} at ${this.canceledData.expireTime}`
        }
      );
    }

    if (this.$route.query.update) {
      this.handleUpdateUrlQuery();
    }
    if (this.$route.query.modalOpen) {
      this.handleEditButtonClick(this.$route.query.modalOpen);
    }
  },
  watch: {
    $route(to, from) {
      if (this.$route.query.update) {
        this.handleUpdateUrlQuery();
      }
    }
  },
  props: {
    cms: {
      type: Object,
      default: () => {
        return {
          page: cms,
          global: require(`@/assets/cms/global.json`)
        };
      }
    },
    inventorySubscriptions: {
      type: Array,
      default: () => {
        return [];
      }
    }
  },
  head() {
    return {
      title: this.cms.page.meta_title,
      meta: this.cms.page.meta
        ? Meta.convertToNuxtMeta(this.cms.page.meta)
        : [],
      script: [
        {
          src: 'https://core.spreedly.com/iframe/iframe-v1.min.js'
        }
      ]
    };
  }
};
</script>
